import styles from './PositionsTable.module.scss';

import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import { Box, IconButton, Tooltip } from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Column, TableInstance } from 'react-table';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';
import { ErpPositionSlMode } from '@work4all/models/lib/Enums/ErpPositionSlMode.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';

import { settings, useSetting } from '../../../../../../../../../../settings';
import { useFormContextPlus } from '../../../../../../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../../../../../../hooks/mask-context';
import { CostCenterCell } from '../../../../../../components/table-cells/CostCenterCell';
import {
  LEDGER_PREFILTER,
  LedgerAccountCell,
} from '../../../../../../components/table-cells/LedgerAccountCell';
import { ErpData } from '../../../../../ErpData';
import {
  ERP_DATE_CELLS,
  ERP_NUMBER_CELLS,
  ERP_TEXT_CELLS,
  ERP_TIME_CELLS,
  ShadowObjectAddPositionArgs,
} from '../../../../../hooks/use-bz-shadow-object-api';
import { useShadowBzObjectApiContext } from '../../../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';
import { EditTableProvider } from '../edit-table/components/edit-table-provider/EditTableProvider';
import { EditTable } from '../edit-table/EditTable';
import {
  EditTableEntry,
  EditTableProps,
  IEditTable,
  OnEditPosition,
} from '../edit-table/types';
import { useSelectedPositionState } from '../hooks/use-selected-position';
import { PositionMask } from '../position-mask/PositionMask';
import { handleOrderChange } from '../utils/handle-order-change';

import { ArticlePickerCell } from './ArticlePickerCell';

export const ForbiddenPriceCell: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('ALERTS.NOT_AUTHORIZED_TO_SEE_PRICES')}>
      <Box textAlign="center">-</Box>
    </Tooltip>
  );
};

export type ForwardedEditTableProps = Pick<
  EditTableProps<Position>,
  | 'showSelectionColumn'
  | 'refs'
  | 'showBorders'
  | 'singleLine'
  | 'allowedColumns'
>;

export type IPositionsTableProps = {
  disabled?: boolean;
  showPositionMask?: boolean;
  positions: Position[];
  onMovePosition: (id: string, index: number) => void;
  onPickPosition?: (article?: Article) => void;
  onRemovePosition: (positionId: string[]) => void;
  onAddPosition?: (props: ShadowObjectAddPositionArgs) => void;
  onEditPosition: (result: OnEditPosition<Position>) => void;
  onCollapsePosition: (result: Position) => void;
  tableInstanceRef: React.RefObject<TableInstance>;
  columns: Column<Position>[];
  refs?: {
    editTable: React.MutableRefObject<IEditTable>;
  };
  total: number;
} & ForwardedEditTableProps;

const editableRows = {
  TITEL: ['longtext'],
  TITELSUMME: ['longtext'],
  ZWISCHENSUMME: ['longtext'],
  TEXTZEILE: ['longtext'],
};

export const PositionsTable = (props: IPositionsTableProps) => {
  const {
    disabled = false,
    positions,
    onAddPosition,
    onEditPosition,
    onCollapsePosition,
    showPositionMask = true,
    showBorders = true,
    columns,
    allowedColumns,
    showSelectionColumn,
    singleLine = true,
    total,
  } = props;

  const { t } = useTranslation();
  const { pending, loading } = useShadowBzObjectApiContext();

  const mask = useMaskContext();
  const hidePrices = mask.entity === Entities.deliveryNote;

  const { watch } = useFormContextPlus<ErpData>();
  const currency = watch('currency');
  const { setSelectedPositionIds } = useSelectedPositionState();

  const renderMobileFooter = useCallback(() => {
    return (
      <tr className={clsx(styles.title, styles.mobilePositionRow)}>
        <td></td>
        <td></td>
        <td className={clsx(styles['tr-border'], styles.mobilePositionRow)}>
          <span style={{ float: 'right' }}>
            {formatNumberAsCurrency(total)} {formatCurrency(currency, t)}
          </span>

          <div style={{ whiteSpace: 'pre-line' }}>{t('COMMON.TOTAL')}</div>
        </td>
      </tr>
    );
  }, [currency, total, t]);

  const renderMobilePosition = useCallback(
    (
      position: Position,
      setEditedPosition: (position: Position) => void,
      allPositions: Position[]
    ) => {
      const key = position.id;

      const bomComponentList = position.posId
        ? allPositions.filter((x) => x.posId === position.posId)
        : null;
      const isLastSubPosition =
        bomComponentList &&
        bomComponentList.findIndex((x) => x.id === position.id) ===
          bomComponentList.length - 1;

      switch (position.positionKind) {
        case ErpPositionsKind.SEITENUMBRUCH:
          return (
            <tr key={key}>
              <td
                colSpan={3}
                className={clsx(styles['tr-border'], styles.mobilePositionRow)}
              >
                <div
                  className={styles['page-break']}
                  onClick={() => {
                    setEditedPosition(position);
                    setSelectedPositionIds([position.id]);
                  }}
                  style={{ width: 'fit-content' }}
                >
                  <InsertPageBreakIcon />
                  <div>{t('COMMON.PAGE_BREAK')}</div>
                </div>
              </td>
            </tr>
          );
        case ErpPositionsKind.TITEL:
        case ErpPositionsKind.TITELSUMME:
        case ErpPositionsKind.ZWISCHENSUMME:
          return (
            <tr
              key={key}
              onClick={() => {
                setEditedPosition(position);
                setSelectedPositionIds([position.id]);
              }}
              className={clsx(styles.title, styles.mobilePositionRow, {
                [styles.text01]:
                  position.positionKind === ErpPositionsKind.TITEL,
              })}
            >
              <td></td>
              <td>{position.number}</td>
              <td style={{ width: '100%' }}>
                {position.positionKind !== ErpPositionsKind.TITEL &&
                  !hidePrices && (
                    <span style={{ float: 'right' }}>
                      {formatNumberAsCurrency(position.totalPriceNet)}{' '}
                      {formatCurrency(currency, t)}
                    </span>
                  )}
                <div style={{ whiteSpace: 'pre-line' }}>
                  {position.longtext}
                </div>
              </td>
            </tr>
          );
        default:
          return (
            <tr key={key} className={styles.mobilePositionRow}>
              <td>
                {[
                  ErpPositionsKind.STUECKLISTE,
                  ErpPositionsKind.INTERNE_STUECKLISTE,
                ].includes(position.positionKind) && (
                  <IconButton
                    size="small"
                    onClick={() => onCollapsePosition(position)}
                    style={{
                      marginTop: '-0.25rem',
                    }}
                  >
                    {allPositions.find((x) => x.posId === position.id) ? (
                      <KeyboardArrowDown />
                    ) : (
                      <KeyboardArrowRight />
                    )}
                  </IconButton>
                )}
              </td>
              <td
                className={styles.positionNumberCell}
                onClick={() => {
                  setEditedPosition(position);
                  setSelectedPositionIds([position.id]);
                }}
              >
                {position.number}
                {position.posId ? (
                  <>
                    <div
                      className={clsx(styles.subPositionLineVert, {
                        [styles.last]: isLastSubPosition,
                      })}
                    ></div>
                    <div className={styles.subPositionLineHor}></div>
                  </>
                ) : null}
              </td>
              <td
                style={{ width: '100%' }}
                onClick={() => {
                  setEditedPosition(position);
                  setSelectedPositionIds([position.id]);
                }}
              >
                <div style={{ whiteSpace: 'pre-line' }}>
                  {position.longtext}
                </div>

                <div className={styles.subline}>
                  <div className={styles.left}>
                    <span>
                      {position.amount} {position.unit}
                    </span>
                    {!hidePrices && (
                      <span
                        className={clsx({
                          [styles.strikethrough]: position.insteadOfTotalPrice,
                        })}
                      >
                        {formatNumberAsCurrency(position.singlePriceNet)}{' '}
                        {formatCurrency(currency, t)}
                      </span>
                    )}
                    {!hidePrices && position.discount > 0 && (
                      <span>{position.discount} %</span>
                    )}
                  </div>
                  {!hidePrices && (
                    <div className={styles.right}>
                      {position.insteadOfTotalPrice ? (
                        position.insteadOfTotalPrice
                      ) : (
                        <>
                          {formatNumberAsCurrency(position.totalPriceNet)}{' '}
                          {formatCurrency(currency, t)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
      }
    },
    [currency, hidePrices, onCollapsePosition, setSelectedPositionIds, t]
  );

  const cellDefinition = useMemo(() => {
    const defs: EditTableProps<Position>['cellDefinition'] = {};

    const skeletonCells = ['totalPriceGross', 'totalPriceNet'];

    skeletonCells.forEach((x) => {
      defs[x] = {
        type: 'number',
        editable: false,
        skeleton: true,
      };
    });

    ERP_DATE_CELLS.forEach((x) => {
      defs[x] = {
        type: 'date',
      };
    });
    ERP_TIME_CELLS.forEach((x) => {
      defs[x] = {
        type: 'time',
      };
    });
    ERP_TEXT_CELLS.forEach((x) => {
      defs[x] = {
        type: 'text',
      };
    });
    ERP_NUMBER_CELLS.forEach((x) => {
      defs[x] = {
        type: 'number',
      };
    });

    const createAccountCell = (
      field: 'ledgerAccount' | 'costAccountEntity'
    ) => ({
      type: 'picker',
      render: (cell: Cell<Position>, { isEditMode }) => (
        <LedgerAccountCell
          label={(value) => value?.number || ''}
          value={cell?.row?.original?.[field]}
          className={clsx(styles.customCell, {
            [styles.justLabel]: !isEditMode,
          })}
          style={{
            margin: '-0.25rem 0 0 0',
            background: isEditMode ? undefined : 'unset',
            border: isEditMode ? undefined : 'unset',
          }}
          onChange={(value) => {
            onEditPosition({
              position: {
                id: cell.row.original.id,
                [cell.column.id]: value?.number || 0,
              },
            });
          }}
          hover
          disabled={disabled}
          overridePrefilter={
            field === 'ledgerAccount' ? LEDGER_PREFILTER : undefined
          }
        />
      ),
    });

    defs.costAccount = createAccountCell('costAccountEntity');
    defs.ledgerAccountNumber = createAccountCell('ledgerAccount');

    defs.costCenter1Number = {
      type: 'picker',
      render: (cell: Cell<Position>, { isEditMode }) => (
        <CostCenterCell
          label={(value) => value?.number || ''}
          value={cell?.row?.original?.costCenter1}
          className={clsx(styles.customCell, {
            [styles.justLabel]: !isEditMode,
          })}
          style={{
            margin: '-0.25rem 0 0 0',
            background: isEditMode ? undefined : 'unset',
            border: isEditMode ? undefined : 'unset',
          }}
          onChange={(value) => {
            onEditPosition({
              position: {
                id: cell.row.original.id,
                [cell.column.id]: value?.number || 0,
              },
            });
          }}
          hover
          disabled={disabled}
        />
      ),
    };

    if (defs.longtext) {
      defs.longtext.shouldRender = (position: Position & EditTableEntry) => {
        return (
          position.cacheOnly && !position.longtext && !position.articleNumber
        );
      };

      defs.longtext.render = (
        _cell: Cell<Position & EditTableEntry>,
        { isEditMode, tableInstance }
      ) => {
        return (
          <ArticlePickerCell
            cell={_cell}
            isEditMode={isEditMode}
            onAddPosition={onAddPosition}
            tableInstance={tableInstance}
          />
        );
      };
    }
    return defs;
  }, [disabled, onEditPosition, onAddPosition]);

  const classes = useMemo(
    () => ({
      mobile: styles.mobilePositionsTable,
    }),
    []
  );

  const loadingRows = useMemo(
    () =>
      (pending.type === 'modifyPosition' &&
        ['singlePriceNet', 'amount', 'discount'].some((x) =>
          pending.properties.includes(x)
        )) ||
      pending.type === 'movePosition'
        ? [pending.positionId]
        : [],
    [pending]
  );

  const decorators = useMemo(() => {
    const getRelation = (position: Position) => {
      if (position.posId) return 'child';
      if (
        position.positionKind === ErpPositionsKind.STUECKLISTE ||
        ('collapsed' in position && position.collapsed)
      ) {
        return 'parent';
      }
      return 'normal';
    };

    const isEditableCell = (cell: string, original: Position) => {
      if (
        (original.positionKind === ErpPositionsKind.INTERNE_POSITION ||
          original.positionKind === ErpPositionsKind.INTERNE_STUECKLISTE) &&
        cell === 'singlePriceNet'
      )
        return false;

      if (
        cell === 'longtext' &&
        original.positionKind === ErpPositionsKind.STUECKLISTE &&
        original.partsListType ===
          ErpPositionSlMode.STUECKLISTE_MIT_SUB_POSITIONEN_IM_LANGTEXT
      )
        return false;

      const relation = getRelation(original);
      if (cell === 'amount' && relation === 'child') {
        return false;
      }
      if (relation === 'parent') {
        return !['partsListAmount', 'singlePriceNet'].includes(cell);
      }

      return true;
    };

    return { getRelation, isEditableCell };
  }, []);

  const onRowOut = useCallback(
    (position: Position, dir: 1 | -1) => {
      const notCachedPositions = positions.filter(
        (x) => !(x as EditTableEntry).cacheOnly
      );
      const isLast =
        notCachedPositions[notCachedPositions.length - 1]?.id === position?.id;
      if (isLast) {
        if (showBorders)
          onAddPosition({
            positionType: ErpPositionsKind.TEXTZEILE,
            article: { id: 0 } as Article,
          });
        setTimeout(() => {
          props.refs.editTable.current.setEditMode('undefined', 'longtext', [
            'longtext',
          ]);
        }, 100);
      } else {
        const positionIndex = notCachedPositions.findIndex(
          (x) => x.id === position?.id
        );
        const nextPosition = notCachedPositions[positionIndex + dir];
        if (nextPosition)
          props.refs.editTable.current.setEditMode(
            nextPosition.id.toString(),
            'longtext'
          );
      }
      // scroll to the left to see longtext popover
      if (props.refs.scroll?.current) props.refs.scroll.current.scrollLeft = 0;
    },
    [
      positions,
      props.refs.scroll,
      props.refs.editTable,
      showBorders,
      onAddPosition,
    ]
  );
  const erpSize = useSetting(settings.erpRowSize());

  const columnSettings = useSetting(settings.erpPositionsConfig(mask.entity));
  const noIdEditableRows = useMemo(
    () => ['longtext', 'amount', 'shortText', 'unit', 'internalText'],
    []
  );

  return !loading ? (
    <Box display="flex" flex="1" flexDirection="column" overflow="hidden">
      <EditTableProvider
        onAdd={props.onAddPosition}
        onRemove={props.onRemovePosition}
        onEdit={onEditPosition}
      >
        <EditTable<Position>
          ref={props.refs.editTable}
          refs={props.refs}
          columns={columns}
          onCollapsePosition={onCollapsePosition}
          editableRows={editableRows}
          cellDefinition={cellDefinition}
          disabled={disabled}
          items={positions}
          renderMobileItem={renderMobilePosition}
          renderMobileFooter={renderMobileFooter}
          tableInstanceRef={props.tableInstanceRef}
          onPick={props.onPickPosition}
          onMove={props.onMovePosition}
          onSelectedItemIdsChange={setSelectedPositionIds}
          mask={showPositionMask ? PositionMask : undefined}
          classes={classes}
          loadingRows={loadingRows}
          itemSize="auto"
          noIdEditableCells={noIdEditableRows}
          decorators={decorators}
          showBorders={showBorders}
          showSelectionColumn={showSelectionColumn}
          allowedColumns={allowedColumns}
          onRowOut={onRowOut}
          columnSettings={columnSettings}
          handleOrderChange={handleOrderChange}
          singleLine={erpSize.value === 'SINGLE' && singleLine}
          entity={Entities.position}
          bordersKey="ERP"
        />
      </EditTableProvider>
    </Box>
  ) : null;
};
